// Phase 1A Step 34 — Rollover week-by-week breakdown table + totals strip.
// Extracted from RolloverTable.tsx for LOC ceiling.

import type { ChainRow } from "./calc-utils.ts";
import { fmtBig } from "./calc-utils.ts";

interface RolloverBreakdownProps {
  rows: ChainRow[];
  weeks: number;
  takeout: number;
  symbol: string;
  protectedTotal: number;
  totalKeptBeforeFinal: number;
  finalWin: number;
}

export function RolloverBreakdown({
  rows,
  weeks,
  takeout,
  symbol,
  protectedTotal,
  totalKeptBeforeFinal,
  finalWin,
}: RolloverBreakdownProps): JSX.Element {
  const tkPct = Math.round(takeout * 100);
  const totals: ReadonlyArray<{ label: string; value: string; accent?: boolean }> = [
    {
      label: `Total taken out (W1–W${Math.max(1, weeks - 1)})`,
      value: `${symbol}${fmtBig(totalKeptBeforeFinal)}`,
    },
    {
      label: `Final week win (W${weeks})`,
      value: `${symbol}${fmtBig(finalWin)}`,
    },
    {
      label: "Grand total in your pocket",
      value: `${symbol}${fmtBig(protectedTotal)}`,
      accent: true,
    },
  ];

  return (
    <div
      className="sz-l-rollover-breakdown-wrap"
      style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
    >
      <div className="sz-l-rollover-breakdown">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "70px 1fr 1fr 1fr 1fr 1.2fr",
            padding: "20px 28px",
            borderBottom: "1px solid var(--border)",
            fontFamily: "var(--mono)",
            fontSize: 10,
            letterSpacing: "0.16em",
            color: "var(--text-dim)",
            textTransform: "uppercase",
          }}
        >
          <span>Week</span>
          <span>Stake</span>
          <span>Win</span>
          <span style={{ color: "var(--success)" }}>Take Out ({tkPct}%)</span>
          <span>Restake</span>
          <span style={{ textAlign: "right" }}>Pocket if you stop</span>
        </div>

        {rows.map((r, i) => {
          const isFinal = i === rows.length - 1;
          return (
            <div
              key={i}
              className="row-line"
              style={{
                gridTemplateColumns: "70px 1fr 1fr 1fr 1fr 1.2fr",
                padding: "16px 28px",
                background: isFinal ? "rgba(212, 168, 67, 0.04)" : "transparent",
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 12,
                  color: isFinal ? "var(--accent)" : "var(--text)",
                  letterSpacing: "0.1em",
                  fontWeight: isFinal ? 600 : 400,
                }}
              >
                W{r.week.toString().padStart(2, "0")}
              </span>
              <span className="mono" style={{ fontSize: 13, color: "var(--text)" }}>
                {symbol}
                {fmtBig(r.stake)}
              </span>
              <span className="mono" style={{ fontSize: 13, color: "var(--text)" }}>
                {symbol}
                {fmtBig(r.win)}
              </span>
              <span className="mono" style={{ fontSize: 13, color: "var(--success)" }}>
                +{symbol}
                {fmtBig(r.kept)}
              </span>
              <span
                className="mono"
                style={{
                  fontSize: 13,
                  color: isFinal ? "var(--text-dim)" : "var(--text-muted)",
                }}
              >
                {isFinal ? "—" : `${symbol}${fmtBig(r.next)}`}
              </span>
              <span
                className="mono"
                style={{
                  fontSize: isFinal ? 17 : 13,
                  color: isFinal ? "var(--accent)" : "var(--text)",
                  fontWeight: isFinal ? 600 : 400,
                  textAlign: "right",
                }}
              >
                {symbol}
                {fmtBig(r.pocketIfStop)}
              </span>
            </div>
          );
        })}

        {/* Totals strip */}
        <div
          className="sz-l-rollover-totals"
          style={{
            padding: "24px 28px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 24,
            background: "rgba(212, 168, 67, 0.04)",
            borderTop: "1px solid var(--accent-deep)",
          }}
        >
          {totals.map((c, i) => (
            <div
              key={i}
              style={{
                borderLeft: i > 0 ? "1px solid var(--border-bright)" : "0",
                paddingLeft: i > 0 ? 24 : 0,
              }}
            >
              <div className="eyebrow-muted" style={{ marginBottom: 8 }}>
                {c.label}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: c.accent ? 24 : 16,
                  color: c.accent ? "var(--accent)" : "var(--text)",
                  fontWeight: c.accent ? 600 : 400,
                  letterSpacing: "0.02em",
                }}
              >
                {c.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
